/**
 * ============================================================
 * LAB ROUTE MAP — Mapa Canônico: Rota + Dialog → Fluxos
 * ============================================================
 * Centraliza o vínculo entre padrões de rota do React Router,
 * IDs de diálogos e os fluxos canônicos do lab_manifest.json.
 *
 * REGRA: qualquer nova rota ou modal criado no sistema DEVE
 * ter entrada aqui. O CI valida este contrato.
 */

export interface RouteFlowMapping {
  /** Padrão de rota (startsWith). Ex: '/master/escolas' */
  routePattern: string;
  /** IDs dos fluxos disponíveis nessa rota */
  flowIds: string[];
}

export interface DialogFlowMapping {
  /** ID declarado no atributo data-dialog-id do componente */
  dialogId: string;
  /** ID do fluxo vinculado */
  flowId: string;
  /** Passo inicial quando o diálogo já está aberto (1-indexed) */
  startStep: number;
}

/**
 * Mapa de Rotas → Fluxos Disponíveis
 * Ordem importa: mais específico primeiro
 */
export const LAB_ROUTE_MAP: RouteFlowMapping[] = [
  // ─── AUTH ───
  { routePattern: '/login', flowIds: ['estadual_acesso'] },
  { routePattern: '/ativar', flowIds: ['ativacao_diretor_convite', 'ativar_diretor', 'ativar_estado', 'ativar_municipio', 'ativar_professor'] },
  { routePattern: '/convite', flowIds: ['ativacao_diretor_convite', 'ativar_diretor'] },
  { routePattern: '/ativacao', flowIds: ['ativacao_diretor_convite', 'ativar_estado', 'ativar_municipio', 'ativar_diretor', 'ativar_professor'] },

  // ─── MASTER / ADMIN GLOBAL ───
  { routePattern: '/master/escolas', flowIds: ['cadastro_de_escolas', 'cadastro_do_diretor_da_escola', 'editar_dados_do_diretor_da_escola'] },
  { routePattern: '/master/estados', flowIds: ['cadastro_de_estados', 'master_cadastro_estado'] },
  { routePattern: '/master/municipios', flowIds: ['cadastro_de_municipios', 'cadastro_do_gestor_do_municipio', 'editar_dados_do_gestor_municipal'] },
  { routePattern: '/master/redes', flowIds: ['cadastro_redes_ensino'] },
  { routePattern: '/master/usuarios', flowIds: ['cadastro_do_diretor_da_escola', 'cadastro_do_gestor_do_municipio', 'editar_dados_do_diretor_da_escola', 'editar_dados_do_gestor_municipal'] },

  // ─── ESTADUAL ───
  { routePattern: '/estadual/dashboard', flowIds: ['estadual_dashboard'] },
  { routePattern: '/estadual/alunos', flowIds: ['estadual_alunos'] },
  { routePattern: '/estadual/comunicados', flowIds: ['estadual_comunicados'] },
  { routePattern: '/estadual/chamadas', flowIds: ['estadual_chamadas'] },
  { routePattern: '/estadual/escolas', flowIds: ['estadual_escolas'] },
  { routePattern: '/estadual/municipios', flowIds: ['estadual_municipios', 'estado_cadastro_municipio'] },
  { routePattern: '/estadual/usuarios', flowIds: ['estadual_usuarios'] },
  { routePattern: '/estadual/configuracoes', flowIds: ['estadual_configuracoes'] },
  { routePattern: '/estadual/perfil', flowIds: ['estadual_perfil'] },
  { routePattern: '/estadual/ead', flowIds: ['estadual_ead'] },

  // ─── MUNICIPAL / PREFEITURAS ───
  { routePattern: '/municipal/dashboard', flowIds: ['gestao_municipal'] },
  { routePattern: '/municipal/escolas', flowIds: ['municipio_cadastro_escola'] },
  { routePattern: '/municipal/financeiro', flowIds: ['dados_financeiros'] },

  { routePattern: '/admin/chamada', flowIds: ['roteiro_chamada', 'alerta_ocorrencia_pais'] },
  { routePattern: '/admin/chamadas-gestao', flowIds: ['roteiro_chamada'] },
  { routePattern: '/admin/alunos/', flowIds: ['ficha_aluno_transferencia', 'cadastro_matriculas'] },
  { routePattern: '/admin/setup', flowIds: ['config_inicial_escola'] },
  { routePattern: '/admin/alunos', flowIds: ['cadastro_matriculas', 'ficha_aluno_transferencia'] },
  { routePattern: '/admin/professores', flowIds: ['recursos_humanos'] },
  { routePattern: '/admin/materias', flowIds: ['cadastro_matriz_materias'] },
  { routePattern: '/admin/turmas', flowIds: ['grade_horaria'] },
  { routePattern: '/admin/equipe', flowIds: ['ativar_equipe'] },
  { routePattern: '/admin/dispositivos', flowIds: ['contingencia_noc'] },
  { routePattern: '/admin/evasao', flowIds: ['gestao_evasao'] },
  { routePattern: '/admin/documentos', flowIds: ['documentos_secretaria'] },
  { routePattern: '/admin/comunicados', flowIds: ['estadual_comunicados'] },
  { routePattern: '/admin/dashboard', flowIds: ['gestao_evasao'] },

  // ─── PROFESSOR ───
  { routePattern: '/professor/chamada', flowIds: ['roteiro_chamada', 'alerta_ocorrencia_pais'] },
  { routePattern: '/professor/notas', flowIds: ['lancamento_notas'] },
  { routePattern: '/professor/avaliacoes', flowIds: ['lancamento_notas'] },

  // ─── EQUIPE (Coordenação / Secretaria) ───
  { routePattern: '/equipe/alunos/', flowIds: ['ficha_aluno_transferencia', 'cadastro_matriculas'] },
  { routePattern: '/equipe/alunos', flowIds: ['cadastro_matriculas', 'ficha_aluno_transferencia'] },
  { routePattern: '/equipe/chamada', flowIds: ['roteiro_chamada', 'alerta_ocorrencia_pais'] },
  { routePattern: '/equipe/frequencia', flowIds: ['roteiro_chamada'] },
  { routePattern: '/equipe/turmas', flowIds: ['grade_horaria'] },
  { routePattern: '/equipe/professores', flowIds: ['recursos_humanos'] },
  { routePattern: '/equipe/responsaveis', flowIds: ['acompanhamento_familiar'] },
  { routePattern: '/equipe/incidentes', flowIds: ['alerta_ocorrencia_pais'] },
  { routePattern: '/equipe/comunicados', flowIds: ['estadual_comunicados'] },
  { routePattern: '/equipe/documentos', flowIds: ['documentos_secretaria'] },
  { routePattern: '/equipe/cobertura', flowIds: ['gestao_evasao'] },
  { routePattern: '/equipe/justificativas', flowIds: ['justificar_falta'] },

  // ─── RESPONSÁVEIS ───
  { routePattern: '/responsavel/dashboard', flowIds: ['acompanhamento_familiar'] },
  { routePattern: '/responsavel/justificativas', flowIds: ['justificar_falta'] },

  // ─── ALUNOS ───
  { routePattern: '/aluno/dashboard', flowIds: ['portal_aluno'] },
  { routePattern: '/aluno/conquistas', flowIds: ['trilha_gamificada'] },
];

/**
 * Mapa de Dialog-ID → Fluxo + Passo Inicial
 * Cada modal deve declarar data-dialog-id no seu elemento raiz.
 */
export const LAB_DIALOG_MAP: DialogFlowMapping[] = [
  { dialogId: 'modal-nova-escola', flowId: 'cadastro_de_escolas', startStep: 2 },
  { dialogId: 'modal-novo-estado', flowId: 'cadastro_de_estados', startStep: 2 },
  { dialogId: 'modal-novo-municipio', flowId: 'cadastro_de_municipios', startStep: 2 },
  { dialogId: 'modal-nova-rede', flowId: 'cadastro_redes_ensino', startStep: 2 },
  { dialogId: 'modal-gestor-escola', flowId: 'cadastro_do_diretor_da_escola', startStep: 2 },
  { dialogId: 'modal-edit-usuario', flowId: 'editar_dados_do_diretor_da_escola', startStep: 2 },
  { dialogId: 'modal-novo-aluno', flowId: 'cadastro_matriculas', startStep: 2 },
  { dialogId: 'modal-ficha-aluno', flowId: 'ficha_aluno_transferencia', startStep: 2 },
  { dialogId: 'modal-transferir-aluno', flowId: 'ficha_aluno_transferencia', startStep: 4 },
  { dialogId: 'modal-nova-materia', flowId: 'cadastro_matriz_materias', startStep: 2 },
  { dialogId: 'modal-nova-turma', flowId: 'grade_horaria', startStep: 2 },
  { dialogId: 'modal-novo-dispositivo', flowId: 'contingencia_noc', startStep: 2 },
  { dialogId: 'modal-novo-comunicado', flowId: 'estadual_comunicados', startStep: 2 },
  { dialogId: 'modal-nova-avaliacao', flowId: 'lancamento_notas', startStep: 2 },
  { dialogId: 'modal-avisar-responsavel', flowId: 'alerta_ocorrencia_pais', startStep: 1 },
  { dialogId: 'modal-nova-justificativa', flowId: 'justificar_falta', startStep: 2 },
  { dialogId: 'modal-convidar-membro', flowId: 'ativar_equipe', startStep: 2 },
  { dialogId: 'modal-emitir-documento', flowId: 'documentos_secretaria', startStep: 2 },
  { dialogId: 'modal-nova-ocorrencia', flowId: 'alerta_ocorrencia_pais', startStep: 1 },
  { dialogId: 'modal-setup-wizard', flowId: 'config_inicial_escola', startStep: 1 },
];

/**
 * Resolve quais fluxos estão disponíveis para um dado padrão de rota.
 * Retorna os IDs dos fluxos ordenados por especificidade (mais específico primeiro).
 */
export function getFlowsForRoute(route: string): string[] {
  const matched = LAB_ROUTE_MAP
    .filter(m => route.startsWith(m.routePattern))
    .sort((a, b) => b.routePattern.length - a.routePattern.length);

  if (matched.length === 0) return [];

  // Retorna os fluxos do match mais específico
  return matched[0].flowIds;
}

/**
 * Resolve qual fluxo e passo inicial estão vinculados a um dialog-id.
 */
export function getFlowForDialog(dialogId: string): DialogFlowMapping | null {
  return LAB_DIALOG_MAP.find(m => m.dialogId === dialogId) ?? null;
}
